import {
  flexRender,
  type Header,
  type OnChangeFn,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import { FunnelIcon } from "lucide-react";

import { cn } from "../../utils";
import { TableHead } from "../table";
import { ColumnFilterPopup } from "./column-filter-popup";
import { getPinningStyles } from "./data-table-row";
import type { FiltersState } from "./filters/types";
import { getColumnSortInfo } from "./helpers";

interface IDataTableHeaderCellProps<TData extends RowData> {
  header: Header<TData, unknown>;
  sorting?: SortingState;
  filters?: FiltersState;
  onFiltersChange?: (f: FiltersState) => void;
  onSortingChange?: OnChangeFn<SortingState>;
}

export function DataTableHeaderCell<TData extends RowData>({
  header,
  sorting,
  filters,
  onFiltersChange,
  onSortingChange,
}: IDataTableHeaderCellProps<TData>) {
  const { column } = header;
  const isPinned = column.getIsPinned();
  const dataLastCol: "left" | "right" | undefined =
    isPinned === "left" && column.getIsLastColumn("left")
      ? "left"
      : isPinned === "right" && column.getIsFirstColumn("right")
        ? "right"
        : undefined;

  const currentSort = column.getIsSorted();
  const ariaSort: "none" | "ascending" | "descending" =
    currentSort === "asc"
      ? "ascending"
      : currentSort === "desc"
        ? "descending"
        : "none";

  const hasFilterSupport = !!column.columnDef.meta?.filterType;
  const isInteractive =
    filters !== undefined &&
    onFiltersChange !== undefined &&
    (column.getCanSort() || hasFilterSupport);

  const isFilterActive = !!filters?.[column.id];

  const withoutThis = (sorting ?? []).filter((s) => s.id !== column.id);
  const filterType = column.columnDef.meta?.filterType;
  const columnType = column.columnDef.meta?.columnType;
  const sortInfo = getColumnSortInfo(filterType, columnType);

  // The label row — becomes interactive only when filter/sort popup is available
  const labelContent = (
    <div className="flex items-center gap-2">
      <div className="flex-1 flex items-center gap-1">
        <div className="break-words text-[15px] font-medium">
          {flexRender(column.columnDef.header, header.getContext())}
        </div>
        {currentSort === "asc" && (
          <sortInfo.asc.icon
            className="shrink-0 opacity-60"
            size={15}
            aria-hidden
          />
        )}
        {currentSort === "desc" && (
          <sortInfo.desc.icon
            className="shrink-0 opacity-60"
            size={15}
            aria-hidden
          />
        )}
      </div>

      {isInteractive && (
        <FunnelIcon
          size={15}
          className={cn("text-primary-400", isFilterActive && "fill-current")}
          strokeWidth={2.5}
        />
      )}
    </div>
  );

  return (
    <TableHead
      key={header.id}
      className={cn(
        "relative min-h-11 bg-white data-[pinned]:backdrop-blur-xs",
        "[&[data-pinned=left][data-last-col=left]]:border-r [&[data-pinned=right][data-last-col=right]]:border-l [&[data-pinned][data-last-col]]:border-gray-200",
        column.columnDef.meta?.headerClassName,
      )}
      colSpan={header.colSpan}
      style={getPinningStyles(column)}
      data-pinned={isPinned || undefined}
      data-last-col={dataLastCol}
      aria-sort={ariaSort}
    >
      {header.isPlaceholder ? null : isInteractive ? (
        <ColumnFilterPopup
          trigger={
            <button
              type="button"
              className="flex w-full min-w-0 items-center gap-1.5 cursor-pointer select-none text-left"
            >
              {labelContent}
            </button>
          }
          column={column}
          columnId={column.id}
          filters={filters}
          onFilterChange={(colId, filter) => {
            const next = { ...filters };
            if (filter === undefined) delete next[colId];
            else next[colId] = filter;
            onFiltersChange(next);
          }}
          onSortAsc={() =>
            onSortingChange?.([...withoutThis, { id: column.id, desc: false }])
          }
          onSortDesc={() =>
            onSortingChange?.([...withoutThis, { id: column.id, desc: true }])
          }
          onSortClear={() => onSortingChange?.(withoutThis)}
          isSortedAsc={currentSort === "asc"}
          isSortedDesc={currentSort === "desc"}
        />
      ) : (
        <div className="flex min-w-0 items-center gap-1">{labelContent}</div>
      )}
    </TableHead>
  );
}
